<template>
    <div>
        <b-row>
            <b-col cols="12" md="1" class="d-flex align-items-center">
                <strong class="ml-3">{{ $t('fdb.filter.search') }}</strong>
            </b-col>
            <b-col cols="12" md="2" class="d-flex align-items-center ">
                <b-form-select :options="statusOptions" :placeholder="$t('fdb.filter.status')" v-model="status" />
            </b-col>
            <b-col cols="12" md="2"><b-form-input :placeholder="$t('fdb.filter.email')" v-model="email"/></b-col>
            <b-col cols="12" md="2" v-if="driverCount">{{ `${driverCount} ${$t('fdb.filter.results')}` }}</b-col>
            <b-col cols="12" md="2">
                <b-button @click="searchDriversWithParams">{{ $t('fdb.filter.search') }}</b-button>
            </b-col>
            <b-col cols="12" md="2" class="ml-auto d-flex">
                <b-button variant="danger" @click="isDriverAddModalVisible = true">{{ $t('fdb.dashboard.add_drivers') }}</b-button>
            </b-col>
        </b-row>
        <b-row class="mt-3">
            <b-table class="fdb-table" hover :items="drivers" :fields="fields">
                <template #cell(status)="data">
                    <status-pill :status="data.item.statusDisplay.type || undefined" :text="$t('fdb.driver_status.'+data.item.statusDisplay.label.toLowerCase()) || undefined" />
                </template>
                <template #cell(email)="data">
                    <strong>{{ data.item.email }}</strong>
                </template>
                <template #cell(number_of_active_subscriptions)="data">
                    <strong>{{ data.item.number_of_active_subscriptions }}</strong>
                </template>
                <template #cell(actions)="data">
                    <div class="d-flex">
                        <template v-if="data.item.statusDisplay.label === 'Pending'">
                            <icon-pill hoverable :id="`driver-${data.item.uuid}-reinvite`" class="mx-1" icon="envelope-open" @click.native="triggerReinvite(data.item.id)"></icon-pill>
                            <b-tooltip :target="`driver-${data.item.uuid}-reinvite`" triggers="hover">
                                {{ $t('fdb.drivers.reinvite_driver') }}
                            </b-tooltip>
                        </template>
                        <template>
                            <icon-pill hoverable :id="`driver-${data.item.uuid}-subscriptions`" class="mx-1" icon="calendar-check" @click.native="redirectToSubscriptions(data.item.email)"></icon-pill>
                            <b-tooltip :target="`driver-${data.item.uuid}-subscriptions`" triggers="hover">
                                {{ $t('fdb.drivers.show_subscriptions') }}
                            </b-tooltip>
                        </template>
                        <template v-if="data.item.driver_status !== 'DELETED' && !data.item.is_whitelabel_user && !data.item.number_of_active_subscriptions">
                            <icon-pill hoverable :id="`driver-${data.item.uuid}-delete`" @click.native="deleteDriver(data.item.uuid)" class="mx-1" icon="trash"></icon-pill>
                            <b-tooltip :target="`driver-${data.item.uuid}-delete`" triggers="hover" >
                                {{ $t('fdb.drivers.delete_driver') }}
                            </b-tooltip>
                        </template>
                        <template v-if="data.item.driver_status === 'INACTIVE'">
                            <icon-pill hoverable :id="`driver-${data.item.uuid}-reactivate`" class="mx-1" @click.native="reactivate(data.item.uuid)" icon="user-check"></icon-pill>
                            <b-tooltip :target="`driver-${data.item.uuid}-reactivate`" triggers="hover">
                                {{ $t('fdb.drivers.reactivate_driver') }}
                            </b-tooltip>
                        </template>
                        <template v-if="data.item.driver_status === 'ACTIVE'">
                            <icon-pill hoverable :id="`driver-${data.item.uuid}-deactivate`" class="mx-1" @click.native="deactivate(data.item.uuid)" icon="user-times"></icon-pill>
                            <b-tooltip :target="`driver-${data.item.uuid}-deactivate`" triggers="hover">
                                {{ $t('fdb.drivers.deactivate_driver') }}
                            </b-tooltip>
                        </template>
                    </div>
                </template>
            </b-table>
        </b-row>
        <driver-add-modal :visible="isDriverAddModalVisible" @hide="isDriverAddModalVisible = false" @driversAdded="getDrivers" />
    </div>
</template>
<script>
import {BTab, BRow, BCol, BFormInput, BTable, BButton, BTooltip, BFormSelect} from 'bootstrap-vue'
import StatusPill from "../atoms/StatusPill.vue";
import IconPill from "../atoms/IconPill.vue";
import DriverAddModal from "../molecules/DriverAddModal.vue";
import StatusMixin from "../../../mixins/StatusMixin.vue";

export default {
    name: "FdbTabDrivers",
    components: {
        IconPill,
        StatusPill,
        BTab, BRow, BCol, BFormInput, BTable, BButton, BTooltip, BFormSelect,
        DriverAddModal
    },
    mixins: [ StatusMixin ],
    data() {
        return {
            isDriverAddModalVisible: false,
            drivers: [],
            status: null,
            email: '',
            fields: [{
                key: 'status', label: this.$t('fdb.filter.status')
            }, {
                key: 'email', label: this.$t('fdb.filter.email')
            }, {
                key: 'number_of_active_subscriptions', label: this.$t('fdb.drivers.active_subscriptions')
            }, {
                key: 'actions', label: this.$t('fdb.actions')
            }],
            statusOptions: [
                { text: this.$t('fdb.filters.select_status'), value: null},
                { text: this.$t('fdb.driver_status.active'), value: "ACTIVE" },
                { text: this.$t('fdb.driver_status.inactive'), value: "INACTIVE" },
                { text: this.$t('fdb.driver_status.deleted'), value: "DELETED" },
                { text: this.$t('fdb.driver_status.pending'), value: "PENDING" }
            ],
        }
    },
    async created() {
        await this.getDrivers()
    },
    methods: {
        mapDrivers(drivers) {
            if (!drivers.length) return []
            return drivers.map(driver => {
                return {
                    ...driver,
                    statusDisplay: this.calculateDriverStatus(driver.driver_status)
                }
            })
        },
        async getDrivers() {
            const response = await this.$axios.get('api/v1/fleetmanager/drivers')
            this.drivers = this.mapDrivers(response.data)
        },
        async reactivate(driverUuid) {
            await this.$axios.put(`api/v1/fleetmanager/drivers/${driverUuid}/reactivate`)
            await this.getDrivers()
        },
        async deactivate(driverUuid) {
            await this.$axios.put(`api/v1/fleetmanager/drivers/${driverUuid}/deactivate`)
            await this.getDrivers()
        },
        async deleteDriver(driverUuid) {
            await this.$axios.put(`api/v1/fleetmanager/drivers/${driverUuid}/delete`)
            await this.getDrivers()
        },
        async searchDriversWithParams() {
            const response = await this.$axios.get(`api/v1/fleetmanager/drivers`, {
                params: {
                    filters: {
                        email: this.email,
                        status: this.status,
                    }
                }
            })
            this.drivers = this.mapDrivers(response.data)
        },
        redirectToSubscriptions(email) {
            this.$emit('setTab', 2)
            this.$emit('setSubscriptionsEmailFilter', email)
        },
        async triggerReinvite(driverId) {
            await this.$axios.put(`api/v1/fleetmanager/drivers/${driverId}/reinvite`)
                .then(() => {
                    alert(this.$t('fdb.driver.reinvite.success'))
                }).catch(() => {
                    alert(this.$t('fdb.driver.reinvite.error'))
            })
        }
    },
    computed: {
        driverCount() {
            return this.drivers.length
        }
    }
}
</script>
